'use client';

import { useQuery } from '@rocicorp/zero/react';
import { FC, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@components/ui/button';
import { useTranslations } from 'next-intl';
import { Command, CommandInput, CommandList } from '@components/ui/command';
import { Dialog, DialogContent, DialogTitle } from '@components/ui/dialog';
import { MOBILE_CHROME_BREAKPOINT, useIsMobile } from '@hooks/use-mobile';
import { useKnowledgeSearch } from '@hooks/use-knowledge-search';
import { useSearchCommand } from '@hooks/use-search-command';
import { useAgentsMembershipEnabled } from '@hooks/use-agents-membership';
import { getUserDisplayName } from '@utils/user-utils';
import { queries } from '~/src/zero/queries';
import { useCurrentOrgUser, useCurrentUser } from '~/src/zero/use-current-user';
import type { SearchHit } from '../../search/types';
import AskAboutThisDialog from './AskAboutThisDialog';
import KnowledgeSearchAnswerCard from './KnowledgeSearchAnswerCard';
import KnowledgeSearchDefaultControls from './KnowledgeSearchDefaultControls';
import KnowledgeSearchMobile from './KnowledgeSearchMobile';
import KnowledgeSearchSuggestions from './KnowledgeSearchSuggestions';
import KnowledgeSearchRow, {
  type KnowledgeSearchRowData
} from './KnowledgeSearchRow';
import KnowledgeSearchSkeletonRow from './KnowledgeSearchSkeletonRow';
import { useKnowledgeSearchNavigation } from './knowledge-search-navigation';

type KnowledgeSearchProps = {
  organization_id: string;
};

/** Uppercase source-type label shown on every row (Outcome hierarchy). */
function useTypeLabels() {
  const t = useTranslations('knowledge-search');
  return {
    recipe: t('recipe'),
    ingredient: t('ingredient'),
    menu: t('menu'),
    reference: t('reference'),
    handbook: t('handbook'),
    person: t('person'),
    channel: t('channel')
  } as const;
}

/**
 * Global knowledge search: replaces the previous name-only recipe/ingredient
 * palette. Desktop keeps the ⌘K dialog; mobile opens a full-screen drawer
 * (KnowledgeSearchMobile). Both share `useKnowledgeSearch` (the HTTP-backed
 * controller) and `KnowledgeSearchRow`. People/channel discovery stays on
 * the existing Zero lexical queries, unchanged, per the plan's Step 3.7.
 */
const KnowledgeSearch: FC<KnowledgeSearchProps> = ({ organization_id }) => {
  const t = useTranslations('knowledge-search');
  const inputRef = useRef<HTMLInputElement>(null);
  const { open, setOpen } = useSearchCommand();
  const isMobile = useIsMobile(MOBILE_CHROME_BREAKPOINT);
  const currentUser = useCurrentUser();
  const orgUser = useCurrentOrgUser(organization_id);
  const [organization] = useQuery(
    queries.organizations.current({ organizationId: organization_id })
  );
  const typeLabels = useTypeLabels();
  const navigate = useKnowledgeSearchNavigation(organization_id, isMobile);

  const search = useKnowledgeSearch({
    organizationId: organization_id,
    open,
    currentUserId: currentUser?.id
  });

  const [highlightedHitId, setHighlightedHitId] = useState<string | null>(null);
  const highlightTimeoutRef = useRef<number | undefined>(undefined);
  const agentsMembershipEnabled = useAgentsMembershipEnabled();
  const [askAboutThisOpen, setAskAboutThisOpen] = useState(false);

  /**
   * A citation click scrolls to the row and briefly highlights it rather
   * than navigating away immediately, so the user can see the answer and
   * its evidence together before committing to open one (plan Step 5.2).
   */
  const highlightHit = (hitId: string) => {
    window.clearTimeout(highlightTimeoutRef.current);
    setHighlightedHitId(hitId);
    document
      .querySelector(`[data-hit-id="${CSS.escape(hitId)}"]`)
      ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    highlightTimeoutRef.current = window.setTimeout(
      () => setHighlightedHitId(null),
      1500
    );
  };

  const committedPeopleChannelTerm =
    search.term.trim().length >= 2 ? search.term.trim() : '';
  const [peopleResults] = useQuery(
    committedPeopleChannelTerm
      ? queries.people.search({
          organizationId: organization_id,
          term: committedPeopleChannelTerm
        })
      : undefined
  );
  const [channelResults] = useQuery(
    committedPeopleChannelTerm
      ? queries.chatChannels.search({
          organizationId: organization_id,
          term: committedPeopleChannelTerm
        })
      : undefined
  );

  const close = () => {
    setOpen(false);
    search.setTerm('');
  };

  const selectHit = (hit: SearchHit) => {
    close();
    navigate(hit);
  };

  const rows: KnowledgeSearchRowData[] = useMemo(() => {
    const knowledgeRows = search.results.map((hit): KnowledgeSearchRowData => ({
      key: `${hit.kind}:${hit.id}`,
      typeLabel: typeLabels[hit.kind as keyof typeof typeLabels] ?? hit.kind,
      title: hit.title,
      subtitle: hit.subtitle,
      onSelect: () => selectHit(hit),
      hitId: hit.id,
      highlighted: hit.id === highlightedHitId
    }));
    const peopleRows = ((peopleResults ?? []) as any[]).map(
      (membership): KnowledgeSearchRowData => ({
        key: `person:${membership.userId}`,
        typeLabel: typeLabels.person,
        title: getUserDisplayName(membership.user ?? {}),
        subtitle: '',
        onSelect: () => {
          close();
          navigate({
            kind: 'person',
            id: membership.userId,
            title: '',
            subtitle: '',
            sourceVersion: 0,
            destination: { kind: 'person', userId: membership.userId }
          });
        }
      })
    );
    const channelRows = ((channelResults ?? []) as any[]).map(
      (channel): KnowledgeSearchRowData => {
        const otherMember = channel.dmChannel
          ? channel.members?.find(
              (member: any) => member.userId !== orgUser?.userId
            )
          : undefined;
        const title = channel.dmChannel
          ? getUserDisplayName(otherMember?.user ?? {})
          : (channel.name ?? '');
        return {
          key: `channel:${channel.id}`,
          typeLabel: typeLabels.channel,
          title,
          subtitle: '',
          onSelect: () => {
            close();
            navigate({
              kind: 'channel',
              id: channel.id,
              title,
              subtitle: '',
              sourceVersion: 0,
              destination: { kind: 'channel', channelId: channel.id }
            });
          }
        };
      }
    );
    return [...knowledgeRows, ...peopleRows, ...channelRows];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    search.results,
    peopleResults,
    channelResults,
    typeLabels,
    orgUser,
    highlightedHitId
  ]);

  const citedSources = useMemo(() => {
    if (!search.answer) return [];
    const hitsById = new Map(search.results.map(hit => [hit.id, hit]));
    return search.answer.citedHitIds
      .map(hitId => hitsById.get(hitId))
      .filter((hit): hit is SearchHit => hit !== undefined)
      .map(hit => ({ kind: hit.kind, id: hit.id, title: hit.title }));
  }, [search.answer, search.results]);

  const onAskAboutThis =
    agentsMembershipEnabled && search.answer
      ? () => setAskAboutThisOpen(true)
      : undefined;

  const loadingLabel = search.isLikelyQuestion
    ? t('findingAnswer')
    : t('loading');

  const emptyMessage =
    search.status === 'loading'
      ? loadingLabel
      : search.status === 'error'
        ? t('error')
        : search.status === 'offline'
          ? t('offline')
          : search.status === 'guidance'
            ? t('guidance')
            : rows.length === 0 && search.effectiveQuery
              ? t('emptyForQuery', { query: search.effectiveQuery })
              : search.term.trim()
                ? t('empty')
                : null;

  const defaultLabel =
    rows.length === 0
      ? null
      : search.mode === 'default'
        ? t('defaultLabel', { query: search.effectiveQuery })
        : search.mode === 'recommended'
          ? t('recommended')
          : null;

  const isOpening = search.term.trim().length === 0;
  const defaultControls = (
    <KnowledgeSearchDefaultControls
      term={search.term}
      savedQuery={search.savedQuery}
      saveStatus={search.saveStatus}
      defaultPending={search.defaultPending}
      onSave={search.saveAsDefault}
      onReset={search.resetDefault}
      onChoose={() => inputRef.current?.focus({ preventScroll: true })}
      onEdit={() => {
        search.setTerm(search.savedQuery ?? '');
        inputRef.current?.focus({ preventScroll: true });
      }}
    />
  );
  const discovery = (
    <div>
      {isOpening ? (
        <div className="px-5 pb-8 pt-1 lg:px-6">
          {defaultControls}
          <KnowledgeSearchSuggestions
            recommendedResults={
              search.mode === 'recommended' ? search.results : []
            }
            onSelect={query => {
              search.setTerm(query);
              // Keep focus inside the dialog when the selected card disappears,
              // without opening the mobile keyboard.
              if (isMobile)
                document
                  .querySelector<HTMLElement>(
                    '[data-knowledge-search-mobile-close]'
                  )
                  ?.focus({ preventScroll: true });
              else inputRef.current?.focus({ preventScroll: true });
            }}
          />
        </div>
      ) : (
        defaultControls
      )}
    </div>
  );

  const askAboutThisDialog = onAskAboutThis ? (
    <AskAboutThisDialog
      organizationId={organization_id}
      open={askAboutThisOpen}
      onOpenChange={setAskAboutThisOpen}
      question={search.effectiveQuery || search.term}
      sources={citedSources}
    />
  ) : null;

  if (isMobile) {
    return (
      <>
        <KnowledgeSearchMobile
          open={open}
          onOpenChange={next => (next ? setOpen(true) : close())}
          organizationName={organization?.name}
          term={search.term}
          onTermChange={search.setTerm}
          onSubmit={search.submit}
          rows={rows}
          emptyMessage={rows.length === 0 ? emptyMessage : null}
          loadingLabel={loadingLabel}
          defaultLabel={defaultLabel}
          inputRef={inputRef}
          discovery={discovery}
          status={search.status}
          onRetry={search.retry}
          answer={search.answer}
          results={search.results}
          onCitationClick={highlightHit}
          onAskAboutThis={onAskAboutThis}
        />
        {askAboutThisDialog}
      </>
    );
  }

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={next => (next ? setOpen(true) : close())}
      >
        <DialogContent
          aria-describedby={undefined}
          className="flex max-h-[85dvh] max-w-[720px] flex-col gap-0 overflow-hidden bg-background p-0"
        >
          <div className="shrink-0 px-6 pb-4 pt-6">
            {organization?.name ? (
              <div className="text-column uppercase tracking-wider text-muted-foreground">
                {organization.name}
              </div>
            ) : null}
            <DialogTitle className="mt-1 pr-10 text-page-title">
              {t('heading')}
            </DialogTitle>
          </div>
          <Command
            shouldFilter={false}
            onKeyDownCapture={event => {
              // Native buttons (clear, save, citations) must not also activate
              // cmdk's selected result. Leave their native click behavior intact.
              if (
                event.key === 'Enter' &&
                event.target instanceof HTMLElement &&
                event.target.closest('button')
              )
                event.stopPropagation();
            }}
            className="min-h-0 flex-1 overflow-hidden rounded-none bg-background"
          >
            <CommandInput
              ref={inputRef}
              aria-label={t('placeholder')}
              wrapperClassName="knowledge-search-field mx-6 mb-4 min-h-12 shrink-0 rounded-full border-0 bg-card pl-4 pr-1"
              trailing={
                search.term ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={t('clearSearch')}
                    onClick={() => {
                      search.setTerm('');
                      inputRef.current?.focus({ preventScroll: true });
                    }}
                  >
                    <X aria-hidden="true" />
                  </Button>
                ) : null
              }
              placeholder={t('placeholder')}
              value={search.term}
              onValueChange={search.setTerm}
              className="h-auto min-w-0 border-0 bg-transparent px-0 py-3 text-body text-[length:var(--text-body)]"
              onKeyDown={event => {
                if (
                  event.key === 'Enter' &&
                  !event.nativeEvent.isComposing &&
                  (search.status === 'loading' || rows.length === 0)
                ) {
                  event.preventDefault();
                  search.submit();
                }
              }}
            />
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain pb-4">
              {discovery}
              {defaultLabel ? (
                <p className="px-6 pb-2 text-caption text-muted-foreground">
                  {defaultLabel}
                </p>
              ) : null}
              {search.answer ? (
                <KnowledgeSearchAnswerCard
                  answerText={search.answer.text}
                  citedHitIds={search.answer.citedHitIds}
                  results={search.results}
                  onCitationClick={highlightHit}
                  onAskAboutThis={onAskAboutThis}
                />
              ) : null}
              <CommandList
                className="max-h-none overflow-visible"
                aria-live="polite"
              >
                {search.status === 'loading' ? (
                  <>
                    <div className="px-4 pb-2 pt-3 text-caption text-muted-foreground">
                      {loadingLabel}
                    </div>
                    <KnowledgeSearchSkeletonRow />
                    <KnowledgeSearchSkeletonRow />
                    <KnowledgeSearchSkeletonRow />
                    <KnowledgeSearchSkeletonRow />
                  </>
                ) : rows.length === 0 ? (
                  emptyMessage ? (
                    <div
                      role={search.status === 'error' ? 'alert' : undefined}
                      className="flex flex-col items-center gap-3 px-6 py-8 text-center text-body text-muted-foreground"
                    >
                      <p>{emptyMessage}</p>
                      {search.status === 'error' ||
                      search.status === 'offline' ? (
                        <Button variant="ghost" onClick={search.retry}>
                          {t('retry')}
                        </Button>
                      ) : null}
                    </div>
                  ) : null
                ) : (
                  rows.map(({ key, ...row }) => (
                    <KnowledgeSearchRow key={key} {...row} />
                  ))
                )}
              </CommandList>
              {search.hasMore ? (
                <div className="border-t border-border px-4 py-2 text-caption text-muted-foreground">
                  {t('refineHint')}
                </div>
              ) : null}
            </div>
          </Command>
        </DialogContent>
      </Dialog>
      {askAboutThisDialog}
    </>
  );
};

export default KnowledgeSearch;
